import { Injectable } from '../../decorators/injectable';
import type {
  ExtractedSupplierCandidate,
  ExtractionResult,
  Supplier,
  VatReviewReason,
} from './goods-receiving.types';

export interface ExtractedSupplierFormData {
  name?: string;
  vatNumber?: string;
  address?: string;
  phone?: string;
  email?: string;
  /** Why the prefilled vatNumber (read off the DDT) must be checked before saving. */
  vatReviewReason?: VatReviewReason;
}

export type ExtractionTerminalAction =
  | { kind: 'noop' }
  | { kind: 'failed' }
  | { kind: 'supplier-matched'; supplier: Supplier }
  | { kind: 'supplier-candidates'; candidates: ExtractedSupplierCandidate[] }
  | { kind: 'supplier-extracted'; data: ExtractedSupplierFormData }
  | { kind: 'no-ddt-recognized' };

export interface DeriveTerminalActionInput {
  extractionStatus: string | undefined;
  supplier: Supplier | null | undefined;
  extractionResult: ExtractionResult | null | undefined;
}

export interface IExtractionTerminalActionService {
  derive(input: DeriveTerminalActionInput): ExtractionTerminalAction;
}

@Injectable()
export class ExtractionTerminalActionService implements IExtractionTerminalActionService {
  derive(input: DeriveTerminalActionInput): ExtractionTerminalAction {
    const { extractionStatus, supplier, extractionResult } = input;
    if (extractionStatus !== 'COMPLETED' && extractionStatus !== 'FAILED') {
      return { kind: 'noop' };
    }
    if (extractionStatus === 'FAILED') {
      return { kind: 'failed' };
    }
    if (supplier) {
      return { kind: 'supplier-matched', supplier };
    }
    const candidates = extractionResult?.supplierMatch?.candidates;
    if (candidates && candidates.length > 0) {
      const first = candidates[0];
      const isAmbiguous =
        candidates.length > 1 || !!first?.existingSupplier || !!first?.isOwnCompany;
      if (isAmbiguous) {
        return { kind: 'supplier-candidates', candidates };
      }
    }
    const extractedSupplier = extractionResult?.supplier;
    if (extractedSupplier?.name) {
      return {
        kind: 'supplier-extracted',
        data: {
          name: extractedSupplier.name ?? undefined,
          vatNumber: extractedSupplier.vatNumber ?? undefined,
          address: extractedSupplier.address ?? undefined,
          phone: extractedSupplier.phone ?? undefined,
          email: extractedSupplier.email ?? undefined,
          vatReviewReason: candidates?.[0]?.vatReviewReason,
        },
      };
    }
    return { kind: 'no-ddt-recognized' };
  }
}
